import * as React from 'react'
import Menu from '@mui/material/Menu'
import { themeConfig } from '@gap/shared/theme'
import { observer } from 'mobx-react-lite'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import AvatarWithName from 'reusable/mui/avatar-with-name/AvatarWithName'
import { useOrgGuardStore } from '../../OrgGuardStore'
import Divider from '@mui/material/Divider'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import { useAuthStore } from 'auth/AuthStore'
import SettingsOutlined from '@mui/icons-material/SettingsOutlined'
import { useLocation } from 'react-router-dom'
import { useRouterStore } from '../../RouterStore'
import { useMainContainerStore } from '../MainContainerStore'
import useStore from 'reusable/mobx-store-utils'
import ProfileMenuStore, { ProfileMenuStoreProvider, useProfileMenuStore } from './ProfileMenuStore'
import Check from '@mui/icons-material/Check'
import useWindowSize from 'reusable/hooks/useWindowSize'
import { getFullNameFromUser } from '@gap/utils/utils'
import { apiClient } from 'api/trpc/trpc-client'
import { messageWarning } from 'reusable/mui/modals/message-warning'
import { messageConfirm } from 'reusable/mui/modals/message-confirm'
import { documentWorkspaceThemeConfig } from 'theme/documentWorkspaceTheme'
import DeleteIcon from '@mui/icons-material/Delete'
import LoginIcon from '@mui/icons-material/Login'
import { snackbarSuccess } from 'reusable/mui/snackbar/snackbar'
import ThemeToggleMenuItem from './ThemeToggleMenuItem'
import DemoChip from 'reusable/mui/chip/DemoChip'

const onHoverProps = {
   ':hover': {
      backgroundColor: themeConfig.primary['200']
   }
}

export default observer(function ProfileMenu() {
   const orgGuardStore = useOrgGuardStore()
   const regionRouterStore = useRegionRouterStore()
   const routerStore = useRouterStore()
   const authStore = useAuthStore()
   const mainContainerStore = useMainContainerStore()
   const location = useLocation()
   const { screenLayout } = useWindowSize()

   const profileMenuStore = useStore(sp => new ProfileMenuStore(sp), { authStore, regionRouterStore, mainContainerStore, orgGuardStore, location, routerStore })

   const { color, invertedColor, iconColor, borderColor } = React.useMemo(() => {
      if (screenLayout === 'mobile') {
         return {
            color: 'white',
            invertedColor: themeConfig.primary['800'],
            iconColor: 'white',
            borderColor: 'rgba(255, 255, 255, 0.3)'
         }
      } else {
         return {
            color: themeConfig.primary['800'],
            invertedColor: 'white',
            iconColor: themeConfig.primary['400'],
            borderColor: themeConfig.primary['400']
         }
      }
   }, [screenLayout])

   return (
      <ProfileMenuStoreProvider store={profileMenuStore}>
         <Box onClick={profileMenuStore.handleClick} sx={{
            '> *': { color },
            border: `1px solid ${borderColor}`,
            ':hover': {
               border: `1px solid ${themeConfig.primary['800']}`
            },
            cursor: 'pointer',
            borderRadius: '8px',
            height: '40px'
         }} data-testid="profile-menu">
            <Stack direction="row" sx={{ px: 1.5, py: 1 }}>
               <Typography sx={{ fontWeight: 400, fontSize: '16px', lineHeight: '24px' }}>
                  {profileMenuStore.org?.name}
               </Typography>
               {profileMenuStore.org?.isDemo && <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}><DemoChip /></Box>}
               <AvatarWithName name={getFullNameFromUser(orgGuardStore.userData)} small sx={{ ml: 1.5, backgroundColor: iconColor, color: invertedColor }} />
            </Stack>
         </Box>

         <Menu
            anchorEl={profileMenuStore.anchorEl}
            id="helper-menu"
            open={profileMenuStore.open}
            onClose={profileMenuStore.handleClose}
            onClick={profileMenuStore.handleClose}
            slotProps={{
               paper: {
                  elevation: 0,
                  sx: {
                     overflow: 'visible',
                     filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                     mt: 1.5,
                     minWidth: '223px',
                  },
               }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            data-testid="profile-menu-popup"
         >
            <Box sx={{ p: 2, cursor: 'pointer', ...onHoverProps, textDecoration: 'none', display: 'flex' }} onClick={profileMenuStore.goToMyProfile}>
               <AvatarWithName name={getFullNameFromUser(orgGuardStore.userData)} sx={{ backgroundColor: themeConfig.primary['800'] }} typographySx={{ fontSize: '20px' }} />
               <Stack sx={{ flex: 1, ml: 2, mt: '-2px' }}>
                  <Typography variant="body1" data-testid="profile-menu-name">{getFullNameFromUser(orgGuardStore.userData)}</Typography>
                  <Typography variant="body2">{orgGuardStore.userData.email}</Typography>
               </Stack>
            </Box>
            <Divider />
            {profileMenuStore.orgs.length > 1 && (
               <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                  {profileMenuStore.orgs.map((org) => (
                     <Box
                        key={`org-selector-${org.id}`}
                        data-id={org.id}
                        sx={{
                           p: 2,
                           ...onHoverProps,
                           cursor: org.id === profileMenuStore.org?.id ? 'default' : 'pointer',
                           display: 'flex',
                           justifyContent: 'space-between',
                           alignItems: 'center',
                        }}
                        onClick={profileMenuStore.anchorElDevMenu ? undefined : profileMenuStore.switchOrg}
                        {...profileMenuStore.canSeeDevMenu ? { onContextMenu: profileMenuStore.handleDevMenuOpen } : {}}
                     >
                        <DevContextMenu orgId={org.id} />
                        <Box sx={{ flex: 1 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body1" sx={{ color: themeConfig.primary[900] }}>
                                 {org.name}
                              </Typography>
                              {org.isDemo && <DemoChip />}
                           </Box>

                           <Typography variant="caption" sx={{ textTransform: 'capitalize', color: themeConfig.primary[600] }}>
                              {org.orgType}
                           </Typography>
                        </Box>

                        {org.id === profileMenuStore.org?.id && <Check />}
                     </Box>
                  ))}
                  <Divider />
               </Box>
            )}
            {orgGuardStore.isDeveloper && [
               <Divider key="theme-toggle-divider" />,
               <ThemeToggleMenuItem key="theme-toggle" />
            ]}
            <Box>
               {profileMenuStore.canSeeOrgSettings && <>
                  <Stack direction="row" sx={{ p: 2, cursor: 'pointer', ...onHoverProps }} onClick={profileMenuStore.goToOrgSettings} data-testid="profile-menu-org-settings">
                     <SettingsOutlined />
                     <Typography variant="body2" sx={{ ml: 4, mt: 0.5, fontWeight: 400 }}>
                        Org settings
                     </Typography>
                  </Stack>
                  <Divider />
               </>}
            </Box>
            <Typography variant="body2" sx={{ p: 2, cursor: 'pointer', ...onHoverProps }} onClick={profileMenuStore.logout}>
               Log out
            </Typography>
         </Menu >
      </ProfileMenuStoreProvider>
   )
})

const DevContextMenu = observer(function DevContextMenu(props: { orgId: string }) {
   const profileMenuStore = useProfileMenuStore()

   const devContextMenuStore = useStore(() => ({
      delete: async () => {
         profileMenuStore.handleDevMenuClose()
         if (!profileMenuStore.canSeeDevMenu) {
            return null
         }
         const org = profileMenuStore.orgs.find(org => org.id === props.orgId)
         if (profileMenuStore.org?.id === props.orgId) {
            messageWarning('Cannot delete user org link when you are using it. Switch to a different org first')
         } else if (await messageConfirm(`Do you really want to delete org ${org?.name}?`, 'Are you sure', { confirmButtonTitle: 'Yes, delete', variant: 'destroy' })) {
            await apiClient.dev.deleteUserOrgAndOrg.mutate({ orgId: props.orgId })
         }
         const userInfo = await apiClient.user.getMyUserInfo.query()
         profileMenuStore.sp.orgGuardStore.setUserInfo(userInfo)
         snackbarSuccess('Done, org switcher list updated')
         return
      },
      goTo: async () => {
         profileMenuStore.handleDevMenuClose()
         await profileMenuStore.sp.orgGuardStore.setOrg(props.orgId)
      }
   }))

   if (!profileMenuStore.canSeeDevMenu) {
      return null
   }

   return <Menu
      anchorEl={profileMenuStore.anchorElDevMenu}
      open={Boolean(profileMenuStore.anchorElDevMenu)}
      onClose={profileMenuStore.handleDevMenuClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
   >
      <Box sx={{ width: '200px', cursor: 'pointer' }}>
         <Box
            sx={{ py: 1, pl: 2, ':hover': { bgcolor: documentWorkspaceThemeConfig.color.hover, borderRadius: '3px' } }}
            onClick={devContextMenuStore.goTo}
         >
            <LoginIcon fontSize="small" sx={{ float: 'left', mr: 2, color: 'green' }} />
            <Typography variant="body2">Go to</Typography>
         </Box>
         <Box
            sx={{ py: 1, pl: 2, ':hover': { bgcolor: documentWorkspaceThemeConfig.color.hover, borderRadius: '3px' } }}
            onClick={devContextMenuStore.delete}
         >
            <DeleteIcon fontSize="small" sx={{ float: 'left', mr: 2, color: 'red' }} />
            <Typography variant="body2">Delete org</Typography>
         </Box>
      </Box>
   </Menu>
})
